import appConstants from "../../../../../appConstants";
import { v4 as uuidv4 } from "uuid";

const getDestinationRouteList = (selectedStops) => {
  return getUniqueLabelValue(
    selectedStops.map((stop) => {
      return { label: stop.routeName, value: stop.routeName, key: uuidv4() };
    })
  );
};

// get object and key value as param 
// return a list of uniques value of that key 
const getListByKey = (selectedStops, key) => {
  const uniqueList = [
    ...new Set(selectedStops.map((stop) => stop[key]).filter(Boolean)),
  ];

  const result = uniqueList.map((stopValue) => ({
    label: stopValue,
    value: stopValue,
    key: uuidv4(),
  }));

  return result;
};

const getCustomerInfoList = (selectedStops) => {
  return getUniqueLabelValue(
    selectedStops.reduce((result, stop) => {
      if (stop.consigneeName) {
        return [
          ...result,
          {
            label: stop.customerCode,
            value: stop.consigneeId,
            key: uuidv4(),
          },
        ];
      }
      return result;
    }, [])
  );
};

const getCustomerInfoListWithName = (selectedStops) => {
  return getUniqueLabelValue(
    selectedStops.reduce((result, stop) => {
      if (stop.consigneeName) {
        return [
          ...result,
          {
            label: `${stop.consigneeName} (${stop.customerCode})`,
            value: stop.consigneeId,
            key: uuidv4(),
          },
        ];
      }
      return result;
    }, [])
  );
};

const getAddressList = (selectedStops) => {
  return getUniqueLabelValue(
    selectedStops.reduce((result, stop) => {
      if (stop.consigneeAddress) {
        return [
          ...result,
          {
            label: `${stop.consigneeAddress}, ${stop.city}, ${stop.state}, ${stop.zip}`,
            value: stop.consigneeId,
            key: uuidv4(),
          },
        ];
      }
      return result;
    }, [])
  );
};

const getPreferredRouteNameList = (customers, selectedStops) => {
  if (!customers) {
    return [];
  }
  const consigneeIdList = selectedStops.map(
    (customer) => customer.consigneeId
  );
  if (!consigneeIdList.length) {
    return [];
  }
  const routeSet = new Set();
  customers.forEach((customer) => {
    const route = customer.deliveryDetails.route;
    if (route && consigneeIdList.includes(customer.deliveryDetails.customerId)) {
      routeSet.add({
        label: route,
        value: route,
        key: uuidv4(),
      });
    }
  });
  return [...routeSet];
};

const getCustomersDeliveryWindowsDetail = (customers, stop) => {
  const customer = customers.find(
    (customer) => customer.deliveryDetails.customerId === stop.consigneeId
  );
  if (!customer) {
    return null;
  }
  let deliveryWindow = "";
  const {
    deliveryDetails: { receivingHours },
  } = customer;
  if (receivingHours) {
    deliveryWindow = formateDeliveryWindowLabel(receivingHours);
  } else {
    deliveryWindow = formateDeliveryWindowLabel({ //this change is specific to ffe, same can be used in develop also
      "mon-fri": stop.deliveryWindow,
    });
  }
  return deliveryWindow ? { label: deliveryWindow, value: stop.consigneeId } : null;
};

// filter out duplicate label value from object list
const getUniqueLabelValue = (list) => {
  const filteredList = list.filter(Boolean);
  return filteredList.filter(
    (a, i) => filteredList.findIndex((s) => a.label === s.label) === i
  );
};

// formatting label into "dayName | time" => "Mon-Fri | 8:00 - 17:00"
const formateDeliveryWindowLabel = (data) => {
  const days = {
    monday: "Mon",
    tuesday: "Tue",
    wednesday: "Wed",
    thursday: "Thu",
    friday: "Fri",
    saturday: "Sat",
    sunday: "Sun",
    "mon-fri": "Mon-Fri",
  };
  return Object.entries(data)
    .map(([key, value]) => {
      return `${days[key]} | ${value}`;
    })
    .join(" \n");
};

const MERGE_TYPES = {
  Stops : "Stops",
  Customers: "Customers"
}

const isPickupStop = (stopType) => {
  return stopType === appConstants.ROUTE_STOP_TYPES.PU;
}

const isDeliveryStop = (stopType) => {
  return !isPickupStop(stopType);
}

const getCustomerName = ({ stopType, shipperName, displayCustomerName }) => {
  return isPickupStop(stopType) ? shipperName : displayCustomerName;
}

const getStopCustomerInfo = (stopData) => {
  if (isPickupStop(stopData.stopType)) {
    return {
      name: stopData.shipperName,
      address: `${stopData.shipperAddress}, ${stopData.shipperCity}`,
    };
  }
  return {
    name: stopData.displayCustomerName,
    address: `${stopData.consigneeAddress}, ${stopData.city}`,
  };
};

const getAdditionalDataPayload = (selectedStops, mergeType, scenarioId) => {
  return {
    scenarioId,
    customers: selectedStops.map((customer) => ({
      customerId: getCustomerId(mergeType, customer),
      routeName: customer.routeName
    }))
  };
};

const getCombinedData = (selectedStops, mergeType, additionalData) => {
  return selectedStops.map((stop) => {
    const moreDetails = additionalData.find(
      (customer) =>
        customer.customerId === getCustomerId(mergeType, stop) &&
        customer.routeName === stop.routeName
    );

    // If the merge type is stops, then the customer ID is consigneeId
    if (mergeType === MERGE_TYPES.Stops) {
      stop.customer = stop.consigneeName;
    }

    return {
      ...stop,
      ...(moreDetails || {})
    };
  });
};

const getCustomerId = (mergeType, stop) => {
  // if merge type is stops, then the customer ID is consigneeId
  return mergeType === MERGE_TYPES.Stops ? stop.consigneeId : stop.customerID;
};

export {
  getUniqueLabelValue,
  getAddressList,
  getCustomerInfoList,
  getCustomerInfoListWithName,
  getDestinationRouteList,
  getListByKey,
  MERGE_TYPES,
  isPickupStop,
  isDeliveryStop,
  getCustomerName,
  getStopCustomerInfo,
  getPreferredRouteNameList,
  getCustomersDeliveryWindowsDetail,
  getAdditionalDataPayload,
  getCombinedData,
};
